import PropTypes from 'prop-types'
import { Component } from 'react'
import { FlagsProvider } from 'flagged'
import { Route, Switch } from 'react-router-dom'
import { CompatRouter } from 'react-router-dom-v5-compat'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import Router from 'shared/universal-router'
import { applyMiddleware, combineReducers, createStore } from 'redux'
import thunk from 'redux-thunk'
import { Provider } from 'react-redux'
import { createLogger } from 'redux-logger'
import {
  registryAddItemsPath,
  registryChecklistPath,
  registryHomePath,
  registryPath,
  registryShopPath,
  organizeRegistryPath,
} from 'lib/urls'
import withReactQueryClientProvider from 'shared/HOC/withReactQueryClientProvider'
import withCurrentUser from 'shared/HOC/withCurrentUser'
import { RECAPTCHA_V3_SITE_KEY } from 'shared/constants'
import onCurrentUserLoaded from 'shared/utils/onCurrentUserLoaded/onCurrentUserLoaded'
import { track, withContextualizedTracking } from 'lib/analytics'
import * as consts from 'registry/constants'
import Home from 'registry/home/registry-home'
import GiftTracker from 'registry/gift-reservations/gift-tracker'
import MobileAddItems from 'registry/MobileAddItems/MobileAddItems'
import GuideViewer from 'registry/home/components/guide-viewer'
import ShopRegistry from 'registry/shop-registry'
import BabyUIThemeProvider from 'baby-ui/foundation/BabyUIThemeProvider'
import Checklist from '../modules/checklist'
import ModalManager from './modal-manager'
import registryReducer from '../reducers'
import * as actions from '../actions'
import RegistryPageContributor from './registry-page-contributor'
import {
  DismissableAboutShopRegistryModal,
  EnableGroupGiftModal,
  MobileFiltersModal,
  OwnerReserveModal,
  OwnerMarkAsPurchasedModal,
  GroupGiftRedemptionModal,
  RegItemDeleteModal,
  RegItemEditModal,
  RedirectModal,
  ContactInfoModal,
  UnreserveModal,
  UnhideAddressModal,
} from '../modals'
import RegistryRootTheme from '../themes/RegistryRootTheme'
import css from './registry-app.scss'
import DisplayGiftManagementFeedbackModal from 'shared/gift-management-feedback-modal/DisplayGiftManagementFeedbackModal'

const middlewares = [thunk]
const REGISTRY_HOME_PATH = '/registry/home'
const MODAL_QUERY_PARAM = 'modal-id'

const modalMetadataMap = {
  BABY_SHOWER_MODAL: { id: 'baby-shower', name: consts.ADD_SHOWER_INFO },
  INSERT_CARDS_MODAL: { id: 'insert-cards', name: consts.ORDER_INSERT_CARDS },
  FAMILY_DETAILS_MODAL: {
    id: 'family-details',
    name: consts.ADD_FAMILY_DETAILS,
  },
}

if (process.env.NODE_ENV === 'development') {
  middlewares.push(createLogger())
}

const initReduxDevTools = () => {
  if (
    typeof window === 'object' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
  ) {
    return window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__()
  }
}

const createStoreWithMiddleware = applyMiddleware(...middlewares)(createStore)

class RegistryApp extends Component {
  constructor(props) {
    super(props)
    this.store = createStoreWithMiddleware(
      combineReducers(registryReducer),
      initReduxDevTools()
    )

    this.store.dispatch(actions.setUser(props.currentUser))
    onCurrentUserLoaded((currentUser) => {
      if (currentUser) {
        this.store.dispatch(actions.setUser(currentUser))
      }
    })

    this.store.dispatch(actions.setRegistry(props.context.registry))
    this.store.dispatch(actions.setInsertCardOrders(props.insertCardOrders))
    this.store.dispatch(
      actions.setIsRegistryDiscountView(props.isRegistryDiscountView)
    )

    this.store.dispatch(actions.setIsContributorView(props.isContributorView))
    this.store.dispatch(actions.setIsMobile(props.context.isMobile))

    if (props.showHelloBabyBoxModal) {
      this.showModal(consts.HELLO_BABY_BOX)
    }

    this.state = {
      isRouterMounted: false,
    }
  }

  getChildContext() {
    const { context } = this.props
    return context
  }

  componentDidMount() {
    const { showInternationalUserModal } = this.props
    if (showInternationalUserModal) {
      setTimeout(() => this.showModal(consts.INTERNATIONAL_USER), 4000)
    }

    const { location } = window
    if (location.pathname === REGISTRY_HOME_PATH) {
      const query = new URLSearchParams(location.search)
      const modalId = query.get(MODAL_QUERY_PARAM)
      this.revealModalById(modalId)
    }
  }

  showModal = (modalName) =>
    this.store.dispatch(actions.setActiveModal(modalName))

  revealModalById = (modalId) => {
    switch (modalId) {
      case modalMetadataMap.BABY_SHOWER_MODAL.id:
        this.showModal(modalMetadataMap.BABY_SHOWER_MODAL.name)
        break
      case modalMetadataMap.FAMILY_DETAILS_MODAL.id:
        this.showModal(modalMetadataMap.FAMILY_DETAILS_MODAL.name)
        break
      case modalMetadataMap.INSERT_CARDS_MODAL.id:
        this.showModal(modalMetadataMap.INSERT_CARDS_MODAL.name)
        break
      default:
    }
  }

  decoratedHomeComponent = () => {
    const { isAdmin, showAppPromoBanner } = this.props
    const { isRouterMounted } = this.state
    return (
      <>
        <ModalManager />
        <Home
          isAdmin={isAdmin}
          showAppPromoBanner={showAppPromoBanner}
          isRouterMounted={isRouterMounted}
        />
        <Route
          path={`${registryHomePath}/view-guide`}
          render={({ history, location }) => (
            <GuideViewer history={history} location={location} />
          )}
        />
      </>
    )
  }

  decoratedRegistryComponent = ({ setOrganizing, location }) => {
    const {
      context: { registry },
      isOwner,
      showAppPromoBanner,
      hasRegistryDiscountEligibleItems,
    } = this.props
    return (
      <>
        <ModalManager />
        <EnableGroupGiftModal />
        <MobileFiltersModal />
        <GroupGiftRedemptionModal />
        <RegItemEditModal />
        <RegItemDeleteModal />
        <OwnerReserveModal />
        <UnreserveModal />
        <UnhideAddressModal />
        <RegistryPageContributor
          isOwner={isOwner}
          setOrganizing={setOrganizing}
          registry={registry}
          showAppPromoBanner={showAppPromoBanner}
          hasRegistryDiscountEligibleItems={hasRegistryDiscountEligibleItems}
          location={location}
        />
      </>
    )
  }

  decoratedShopRegistryComponent = () => {
    const {
      context: { registry },
    } = this.props
    return (
      <>
        <ModalManager />
        <GroupGiftRedemptionModal />
        <OwnerMarkAsPurchasedModal />
        <DismissableAboutShopRegistryModal />
        <RedirectModal />
        <ContactInfoModal />
        <ShopRegistry registry={registry} />
      </>
    )
  }

  decoratedMobileAddItemsComponent = () => {
    const { isRouterMounted } = this.state
    return (
      <>
        <ModalManager />
        <MobileAddItems isRouterMounted={isRouterMounted} />
      </>
    )
  }

  render() {
    const {
      context: {
        registry: { urlSlug },
      },
      featureFlags,
      path,
    } = this.props
    const theme = new RegistryRootTheme()

    return (
      <Provider store={this.store}>
        <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_V3_SITE_KEY}>
          <FlagsProvider features={featureFlags}>
            <BabyUIThemeProvider theme={theme}>
              <Router
                location={{ pathname: path }}
                isMountedCallback={(isRouterMounted) => {
                  this.setState({ isRouterMounted })
                }}
              >
                <CompatRouter>
                  <Switch>
                    <Route
                      path={registryHomePath}
                      component={this.decoratedHomeComponent}
                    />
                    <Route
                      path={registryPath(urlSlug)}
                      render={({ location }) => {
                        const setOrganizing =
                          location.pathname === organizeRegistryPath(urlSlug)
                        return this.decoratedRegistryComponent({
                          setOrganizing,
                          location,
                        })
                      }}
                    />
                    <Route
                      path={registryAddItemsPath}
                      component={this.decoratedMobileAddItemsComponent}
                    />
                    <Route
                      path={registryShopPath}
                      component={this.decoratedShopRegistryComponent}
                    />
                    <Route
                      path="/registry/:id?/gift-tracker"
                      component={() => (
                        <>
                          <GiftTracker />
                          <DisplayGiftManagementFeedbackModal
                            registry={this.props.context.registry}
                            eventLocation={track.EventLocation.GIFT_TRACKER}
                          />
                          <ModalManager />
                        </>
                      )}
                    />
                    <Route component={Checklist} path={registryChecklistPath} />
                  </Switch>
                </CompatRouter>
              </Router>
            </BabyUIThemeProvider>
          </FlagsProvider>
        </GoogleReCaptchaProvider>
      </Provider>
    )
  }
}

RegistryApp.propTypes = {
  context: PropTypes.shape({
    bookmarkletJS: PropTypes.string.isRequired,
    enableInaccuracyReportForm: PropTypes.bool.isRequired,
    isFacebookApp: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    registry: PropTypes.object.isRequired,
    showExtensionInstall: PropTypes.bool,
  }).isRequired,
  currentUser: PropTypes.object,
  featureFlags: PropTypes.object.isRequired,
  hasRegistryDiscountEligibleItems: PropTypes.bool,
  isAdmin: PropTypes.bool.isRequired,
  isOwner: PropTypes.bool.isRequired,
  insertCardOrders: PropTypes.array.isRequired,
  isContributorView: PropTypes.bool.isRequired,
  isRegistryDiscountView: PropTypes.bool,
  path: PropTypes.string.isRequired,
  showAppPromoBanner: PropTypes.bool.isRequired,
  showHelloBabyBoxModal: PropTypes.bool.isRequired,
  showInternationalUserModal: PropTypes.bool,
}

RegistryApp.defaultProps = {
  hasRegistryDiscountEligibleItems: false,
  isRegistryDiscountView: false,
  showInternationalUserModal: false,
  currentUser: undefined,
}

RegistryApp.childContextTypes = {
  bookmarkletJS: PropTypes.string,
  showExtensionInstall: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isFacebookApp: PropTypes.bool,
  isMobile: PropTypes.bool,
  enableInaccuracyReportForm: PropTypes.bool,
  registry: PropTypes.object.isRequired,
}

export default withContextualizedTracking({
  eventLocation: track.EventLocation.REGISTRY,
})(withReactQueryClientProvider(withCurrentUser(RegistryApp)))
